// frontend/src/components/LocationEditModal.jsx
// Project: Vision ANPR by Castle Traffic Ltd
// Updated: 2026-11-04 11:42 GMT
//  • Added explicit edit mode: load stored location, position map on saved pin, PUT changes to same ID.
//  • Preserved create mode and existing Leaflet drag/click + authority lookup.
// Purpose: Universal "Create Location" modal (name/postcode lookup, "My location",
//          Leaflet map, and safe POST with tenant_id). Theme-aware buttons.
// Notes:
//  • PORTAL to document.body so it never gets clipped by parent containers.
//  • Uses only DB-safe keys: tenant_id, name, address_line_1/2, city, county,
//    postcode, country, lat, lon, timezone.
//  • Geocode: if value looks like a postcode, try /api/geocode?postcode=…,
//    otherwise skip backend and use Nominatim directly (prevents your 404 spam).
//  • Create: tries POST /api/locations/ → /api/locations → /api/location/ → /api/location.
//  • Map: plain Leaflet for React 18/19 compatibility.
//  • Ensure 'leaflet/dist/leaflet.css' is imported once in your app root.
//
// Responsive pass (2026-09-16):
//  • Modal fits narrow/mobile viewports without changing portal or z-index behaviour.
//  • Header/footer remain visible while the body scrolls independently.
//  • Search/actions, authority metadata, badges and footer controls wrap safely.
//  • Leaflet map height adapts to smaller screens while preserving map behaviour.
//  • No geocoding, geolocation, authority lookup, tenant payload or save logic changed.
//
// Stacking correction (2026-09-29):
//  • Raised the body-portaled overlay above GlobalJobCreateFlyout and parent modals.
//  • Allows Create Location to remain interactive when launched from nested job creation.
//  • No location, geocoding, authority lookup or save behaviour changed.
//
// Stability and UK lookup correction (2026-09-30):
//  • Restrict Nominatim searches to GB and reject out-of-UK postcode results.
//  • Only dismiss on a genuine backdrop pointer interaction; isolate portal clicks.
//  • Dispose Leaflet map on close/unmount and re-create/invalidate on every open.
//  • Ignore stale geocode results and abort searches when closing.
//
// Authority hierarchy display (2026-09-30):
//  • Display the active ONS upper-tier county/unitary relationship returned by
//    geo/admin-areas/by-point; identify authorities with no separate upper tier.
//  • Authority panel only; map, geocoding, dismissal and save logic unchanged.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import axios from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ---------- Layout (kept consistent with ResourceEditModal look) ----------
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 8,
  boxSizing: "border-box",
  // ↑ Ensure this stacks ABOVE JobEditModal/ModalWrapper
  zIndex: 151000,
};

const modal = {
  position: "relative",
  width: "min(980px, calc(100vw - 16px))",
  maxHeight: "calc(100dvh - 16px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};

const header = {
  padding: "12px 52px 12px 16px",
  borderBottom: "1px solid #eee",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  flexShrink: 0,
  minWidth: 0,
};

const body = {
  padding: "clamp(12px, 2vw, 16px)",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
};
const footer = {
  padding: "12px 14px",
  borderTop: "1px solid #eee",
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  flexWrap: "wrap",
  flexShrink: 0,
  background: "#fff",
};

const label = { display: "block", fontSize: 12, color: "#555", marginBottom: 4 };
const inputStyle = {
  height: 34,
  padding: "6px 8px",
  border: "1px solid #ccc",
  borderRadius: 6,
  width: "100%",
  background: "#fff",
};
const selectStyle = { ...inputStyle, height: 36 };

const card = {
  minWidth: 0,
  border: "1px solid #e5e5e5",
  borderRadius: 10,
  padding: 12,
  background: "#fafafa",
};

const badgeBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  maxWidth: "100%",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  border: "1px solid transparent",
};

const isLikelyPostcode = (s) => {
  if (!s) return false;
  const v = String(s).trim().toUpperCase();
  // very permissive UK-ish check; we only use to decide backend vs nominatim
  return /^[A-Z]{1,2}\d[A-Z0-9]?\s*\d[A-Z]{2}$/.test(v);
};

// Approximate UK extent used as a defensive guard for coordinates returned by
// postcode geocoding and device location. Nominatim itself is country-filtered.
const isWithinUKExtent = (lat, lon) =>
  Number.isFinite(Number(lat)) && Number.isFinite(Number(lon)) &&
  Number(lat) >= 49.8 && Number(lat) <= 60.95 &&
  Number(lon) >= -8.7 && Number(lon) <= 2.1;

const DEFAULT = {
  name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  county: "",
  postcode: "",
  country: "United Kingdom",
  lat: 51.5074,
  lon: -0.1278,
  timezone: "Europe/London",
};

const LocationEditModal = ({ open, onClose, onCreated, onUpdated, initial = {}, locationId = null }) => {
  const editing = locationId !== null && locationId !== undefined && locationId !== "";
  const { user } = useAuth();

  const [form, setForm] = useState({ ...DEFAULT, ...initial });
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [recordReady, setRecordReady] = useState(false);

  // ---- Authority auto-lookup state ----
  const [adminArea, setAdminArea] = useState(null); // {gss_code,name,country_code,area_type} | null
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState("");

  const [authorityEntry, setAuthorityEntry] = useState(null); // authority_directory row (tenant-scoped) | null
  const [dirBusy, setDirBusy] = useState(false);
  const [dirError, setDirError] = useState("");
  const [dirNotConfigured, setDirNotConfigured] = useState(false);

  // Map refs
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const backdropPointerRef = useRef(false);
  const geocodeSequenceRef = useRef(0);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Fetch server truth on every edit opening. Do not seed the map with London's
  // create defaults while the existing Location coordinates are loading.
  useEffect(() => {
    if (!open) { setRecordReady(false); return undefined; }
    let active = true;
    setRecordReady(false);
    setErrorText("");
    setSearch("");
    if (!editing) {
      setForm({ ...DEFAULT, ...initial });
      setRecordReady(true);
      return () => { active = false; };
    }
    axios.get(`/locations/${locationId}`, { baseURL: "/api" })
      .then(({ data }) => {
        if (!active) return;
        if (!data || Number(data.id) !== Number(locationId)) throw new Error("Location not found.");
        setForm({ ...DEFAULT, ...data });
        setRecordReady(true);
      })
      .catch((err) => {
        if (!active) return;
        setErrorText(err?.response?.data?.detail || err?.message || "Unable to load Location.");
      });
    return () => { active = false; };
    // initial is a create-mode seed. Only opening/ID changes should trigger a reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId]);

  // ---- Map init / sync ----
  const initMapOnce = () => {
    if (!open || !recordReady || mapObjRef.current || !mapRef.current) return;

    const lat = Number(form.lat) || DEFAULT.lat;
    const lon = Number(form.lon) || DEFAULT.lon;

    const map = L.map(mapRef.current);
    map.setView([lat, lon], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([lat, lon], { draggable: true }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      setField("lat", Number(p.lat.toFixed(6)));
      setField("lon", Number(p.lng.toFixed(6)));
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      setField("lat", Number(e.latlng.lat.toFixed(6)));
      setField("lon", Number(e.latlng.lng.toFixed(6)));
    });

    mapObjRef.current = map;
    markerRef.current = marker;
  };

  const centerOn = (lat, lon, zoom = 15) => {
    const map = mapObjRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const target = L.latLng(lat, lon);
    marker.setLatLng(target);
    map.setView(target, zoom, { animate: true });
  };

  useEffect(() => {
    if (!open || !recordReady) return;
    // The portal has committed its map container before passive effects run.
    const t = window.setTimeout(() => {
      initMapOnce();
      mapObjRef.current?.invalidateSize({ pan: false });
    }, 0);
    return () => {
      window.clearTimeout(t);
      // Leaflet stores an ID on the DOM node. Remove the old map before a
      // subsequent opening creates a new node, including in React StrictMode.
      markerRef.current = null;
      if (mapObjRef.current) {
        mapObjRef.current.off();
        mapObjRef.current.remove();
        mapObjRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordReady]);

  useEffect(() => {
    if (!open) {
      geocodeSequenceRef.current += 1;
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = null;
      return;
    }
    // Prevent the underlying Job Edit modal's window-level Escape listener
    // from dismissing the parent while this child modal is active.
    const interceptEscape = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      }
    };
    document.addEventListener("keydown", interceptEscape, true);
    return () => {
      document.removeEventListener("keydown", interceptEscape, true);
      geocodeSequenceRef.current += 1;
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    const map = mapObjRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    if (form.lat == null || form.lon == null) return;
    const next = L.latLng(Number(form.lat), Number(form.lon));
    const cur = marker.getLatLng();
    if (Math.abs(cur.lat - next.lat) > 1e-6 || Math.abs(cur.lng - next.lng) > 1e-6) {
      marker.setLatLng(next);
      map.panTo(next, { animate: true });
    }
  }, [form.lat, form.lon]);

  // ---- Geocode helpers ----
  const geocodeAbortRef = useRef(null);

  const geocodeBackendPostcode = async (postcode) => {
    try {
      const ctrl = new AbortController();
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = ctrl;
      const r = await axios.get("/geocode", {
        baseURL: "/api",
        params: { postcode },
        signal: ctrl.signal,
      });
      const d = r?.data;
      if (d?.lat != null && d?.lon != null && isWithinUKExtent(d.lat, d.lon)) {
        return { lat: Number(d.lat), lon: Number(d.lon), label: d.label || postcode };
      }
      if (Array.isArray(d) && d.length && d[0]?.lat != null && d[0]?.lon != null && isWithinUKExtent(d[0].lat, d[0].lon)) {
        return {
          lat: Number(d[0].lat),
          lon: Number(d[0].lon),
          label: d[0].display_name || d[0].label || postcode,
        };
      }
    } catch {
      /* ignore (we'll fallback) */
    }
    return null;
  };

  const geocodeNominatim = async (q) => {
    try {
      const ctrl = new AbortController();
      geocodeAbortRef.current?.abort();
      geocodeAbortRef.current = ctrl;
      const params = new URLSearchParams({
        format: "json",
        limit: "5",
        countrycodes: "gb",
        q,
      });
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?${params.toString()}`,
        { signal: ctrl.signal, headers: { "Accept-Language": "en-GB" } }
      );
      if (!resp.ok) return null;
      const arr = await resp.json();
      const match = Array.isArray(arr)
        ? arr.find((item) => isWithinUKExtent(item?.lat, item?.lon))
        : null;
      if (match) {
        return {
          lat: Number(match.lat),
          lon: Number(match.lon),
          label: match.display_name || q,
        };
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const handleSearch = async () => {
    setErrorText("");
    const q = (search || form.postcode || "").trim();
    if (!q) return;

    const requestId = ++geocodeSequenceRef.current;
    geocodeAbortRef.current?.abort();
    let out = null;
    if (isLikelyPostcode(q)) {
      // Try your backend first ONLY for postcodes (prevents /geocode?q=… 404 spam)
      out = await geocodeBackendPostcode(q);
      if (!out) out = await geocodeNominatim(q);
    } else {
      // Free-text place name — go straight to Nominatim
      out = await geocodeNominatim(q);
    }

    if (requestId !== geocodeSequenceRef.current) return;
    if (out && isWithinUKExtent(out.lat, out.lon)) {
      setField("lat", Number(out.lat.toFixed(6)));
      setField("lon", Number(out.lon.toFixed(6)));
      if (!form.name) setField("name", out.label?.split(",")[0] || "New Location");
      centerOn(out.lat, out.lon, 15);
    } else {
      setErrorText("No UK match found. Try a more specific UK place name or postcode.");
    }
  };

  const handleMyLocation = () => {
    setErrorText("");
    if (!navigator.geolocation) {
      setErrorText("Browser location is unavailable.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords || {};
        if (isWithinUKExtent(latitude, longitude)) {
          setField("lat", Number(latitude.toFixed(6)));
          setField("lon", Number(longitude.toFixed(6)));
          if (!form.name) setField("name", "On-Site Location");
          centerOn(latitude, longitude, 16);
        } else {
          setErrorText("Your device location is outside the supported UK area.");
        }
      },
      (err) => setErrorText(err?.message || "Failed to get current position."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ---- Authority auto-lookup (debounced) ----
  const adminAbortRef = useRef(null);
  const dirAbortRef = useRef(null);
  const authDebounceRef = useRef(null);

  const cancelAuthorityRequests = () => {
    try {
      if (adminAbortRef.current) adminAbortRef.current.abort();
    } catch {
      /* ignore */
    }
    try {
      if (dirAbortRef.current) dirAbortRef.current.abort();
    } catch {
      /* ignore */
    }
    adminAbortRef.current = null;
    dirAbortRef.current = null;
  };

  const resetAuthorityState = () => {
    setAdminArea(null);
    setAdminBusy(false);
    setAdminError("");
    setAuthorityEntry(null);
    setDirBusy(false);
    setDirError("");
    setDirNotConfigured(false);
  };

  const loadAuthorityByPoint = async (lat, lon) => {
    setAdminBusy(true);
    setAdminError("");
    setDirError("");
    setDirNotConfigured(false);
    setAuthorityEntry(null);

    try {
      const ctrl = new AbortController();
      adminAbortRef.current = ctrl;

      const r = await axios.get("/geo/admin-areas/by-point", {
        baseURL: "/api",
        params: { lat, lng: lon },
        signal: ctrl.signal,
      });

      const area = r?.data || null;

      if (!area || !area.gss_code) {
        setAdminArea(null);
        setAdminBusy(false);
        setAuthorityEntry(null);
        setDirNotConfigured(false);
        return;
      }

      setAdminArea(area);
      setAdminBusy(false);

      // Tenant-scoped directory lookup by gss_code
      setDirBusy(true);
      const ctrl2 = new AbortController();
      dirAbortRef.current = ctrl2;

      const r2 = await axios.get("/authority-directory", {
        baseURL: "/api",
        params: { q: area.gss_code, limit: 1 },
        signal: ctrl2.signal,
      });

      const d2 = r2?.data;

      let row = null;
      if (Array.isArray(d2)) row = d2[0] || null;
      else if (Array.isArray(d2?.items)) row = d2.items[0] || null;
      else if (d2 && typeof d2 === "object" && d2.id) row = d2;

      if (!row) {
        setAuthorityEntry(null);
        setDirNotConfigured(true);
      } else {
        setAuthorityEntry(row);
        setDirNotConfigured(false);
      }
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      // Split failures: admin lookup is reference; directory is tenant. Treat both as non-fatal for this modal.
      if (!adminArea) {
        setAdminError(status && detail ? `${status} — ${detail}` : "Failed to resolve authority for this coordinate.");
      } else {
        setDirError(status && detail ? `${status} — ${detail}` : "Failed to load authority directory entry.");
      }
    } finally {
      setAdminBusy(false);
      setDirBusy(false);
    }
  };

  useEffect(() => {
    if (!open || !recordReady) return;

    // Clear previous authority immediately on coordinate change
    cancelAuthorityRequests();
    resetAuthorityState();

    const lat = form.lat;
    const lon = form.lon;
    const latNum = typeof lat === "number" ? lat : Number(lat);
    const lonNum = typeof lon === "number" ? lon : Number(lon);

    if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return;

    if (authDebounceRef.current) clearTimeout(authDebounceRef.current);

    authDebounceRef.current = setTimeout(() => {
      loadAuthorityByPoint(latNum, lonNum);
    }, 300);

    return () => {
      if (authDebounceRef.current) clearTimeout(authDebounceRef.current);
      cancelAuthorityRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordReady, form.lat, form.lon]);

  // ---- Save (POST /locations… with tenant_id) ----
  const postToAny = async (payload) => {
    const candidates = ["/locations/", "/locations", "/location/", "/location"];
    let lastErr;
    for (const path of candidates) {
      try {
        const r = await axios.post(path, payload, {
          baseURL: "/api",
          headers: { "Content-Type": "application/json" },
        });
        if (r?.data?.id) return r.data;
      } catch (e) {
        lastErr = e;
        // If 405 (Method Not Allowed) or 404, try the next candidate
        const code = e?.response?.status;
        if (code !== 404 && code !== 405) break;
      }
    }
    // throw last captured error to surface detail
    throw lastErr || new Error("All location endpoints failed.");
  };

  const save = async () => {
    if (!recordReady) return;
    setBusy(true);
    setErrorText("");
    if (!isWithinUKExtent(form.lat, form.lon)) {
      setBusy(false);
      setErrorText("Select coordinates within the supported UK area before saving the location.");
      return;
    }

    const tenant_id = user?.tenant_id ?? null;
    const name =
      form.name?.trim() ||
      (form.postcode?.trim() ? `Site ${form.postcode.trim()}` : "New Location");

    if (!tenant_id) {
      setBusy(false);
      setErrorText("Missing tenant_id; please re-login.");
      return;
    }
    if (!name) {
      setBusy(false);
      setErrorText("Please enter a Location Name.");
      return;
    }

    const payload = {
      ...(editing ? {} : { tenant_id }),
      name,
      address_line_1: form.address_line_1 || null,
      address_line_2: form.address_line_2 || null,
      city: form.city || null,
      county: form.county || null,
      postcode: form.postcode || null,
      country: form.country || null,
      lat: form.lat === "" ? null : typeof form.lat === "number" ? form.lat : Number(form.lat) || null,
      lon: form.lon === "" ? null : typeof form.lon === "number" ? form.lon : Number(form.lon) || null,
      timezone: form.timezone || null,
    };

    try {
      if (editing) {
        if (!window.confirm(
          "Update this existing Location? If it is shared by other Jobs, its new position will also apply to those Jobs."
        )) return;
        const response = await axios.put(`/locations/${locationId}`, payload, { baseURL: "/api" });
        if (!response?.data || Number(response.data.id) !== Number(locationId)) {
          throw new Error("Location update response did not match the original ID.");
        }
        onUpdated?.(response.data);
      } else {
        const created = await postToAny(payload);
        onCreated?.(created);
      }
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      let msg = e?.message || (editing ? "Update failed." : "Create failed.");
      if (status === 405) msg = "405 — Method Not Allowed";
      else if (status === 404) msg = "404 — Endpoint not found";
      else if (status === 422 && data?.detail) {
        msg = Array.isArray(data.detail)
          ? data.detail.map((d) => `${d.loc?.slice(1).join(".")}: ${d.msg}`).join("; ")
          : String(data.detail);
      } else if (status && data?.detail) {
        msg = `${status} — ${String(data.detail)}`;
      } else if (status) {
        msg = `${status} — ${msg}`;
      }
      setErrorText(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const accentColor = "var(--accent-color, #ff7a00)";

  const activeBadge = (
    <span
      style={{
        ...badgeBase,
        background: "#e8f5e9",
        borderColor: "#c8e6c9",
        color: "#1b5e20",
      }}
    >
      ● Active
    </span>
  );

  const inactiveBadge = (
    <span
      style={{
        ...badgeBase,
        background: "#ffebee",
        borderColor: "#ffcdd2",
        color: "#b71c1c",
      }}
    >
      ● Inactive
    </span>
  );

  const warnBadge = (text) => (
    <span
      style={{
        ...badgeBase,
        background: "#fff8e1",
        borderColor: "#ffecb3",
        color: "#8a6d00",
      }}
    >
      ⚠ {text}
    </span>
  );

  const infoBadge = (text) => (
    <span
      style={{
        ...badgeBase,
        background: "#e3f2fd",
        borderColor: "#bbdefb",
        color: "#0d47a1",
      }}
    >
      {text}
    </span>
  );

  const safeUrl = (u) => {
    if (!u) return null;
    const s = String(u).trim();
    if (!s) return null;
    // tolerate missing protocol (users paste "www.")
    if (/^https?:\/\//i.test(s)) return s;
    return "https://" + s.replace(/^\/+/, "");
  };

  return createPortal(
    <div
      style={overlay}
      aria-modal="true"
      role="dialog"
      onPointerDown={(e) => {
        backdropPointerRef.current = e.target === e.currentTarget;
      }}
      onPointerUp={(e) => {
        const genuineBackdropClick = backdropPointerRef.current && e.target === e.currentTarget;
        backdropPointerRef.current = false;
        if (genuineBackdropClick && !busy) onClose?.();
      }}
      onPointerCancel={() => { backdropPointerRef.current = false; }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        {/* Close pill (theme-aware) */}
        <button
          className="
              absolute top-2 right-2 z-10
              w-8 h-8 sm:w-7 sm:h-7
              rounded-full
              text-white text-sm font-bold
              flex items-center justify-center
              opacity-100 sm:opacity-70 sm:hover:opacity-100
              transition-opacity duration-200
              shadow-md
            "
          style={{ backgroundColor: "var(--secondary-color)" }}
          onClick={onClose}
          title="Close"
        >
          ×
        </button>

        {/* Header */}
        <div style={header}>
          <div className="min-w-0 font-extrabold break-words">{editing ? `Edit Location #${locationId}` : "Create Location"}</div>
          {/* keep header clean; close button is the purple pill above */}
        </div>

        {/* Body */}
        <div style={body}>
          {!recordReady && <div className="mb-3 text-sm text-gray-600">{errorText || "Loading location…"}</div>}
          {recordReady && <>
          {/* Search row */}
          <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label style={label}>Search by place name</label>
              <input
                style={inputStyle}
                placeholder="e.g., Tesco Extra Canary Wharf"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="mt-1 text-xs text-gray-500 break-words">
                Searches are restricted to United Kingdom locations.
              </div>
            </div>
            <div>
              <label style={label}>Postcode</label>
              <input
                style={inputStyle}
                placeholder="e.g., SW1A 1AA"
                value={form.postcode}
                onChange={(e) => setField("postcode", e.target.value)}
              />
              <div className="text-xs text-gray-500 mt-1">
                UK postcodes use the backend geocoder first, then UK-only map lookup.
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 md:self-end">
              <button
                type="button"
                onClick={handleSearch}
                className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick"
                title="Find on map"
              >
                Find on Map
              </button>
              <button
                type="button"
                onClick={handleMyLocation}
                className="w-full rounded bg-btnGeneralNormal px-3 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                title="Use current device location"
              >
                My Location
              </button>
            </div>
          </div>

          {/* Name + address */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label style={label}>Friendly Name *</label>
              <input
                style={inputStyle}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g., Tesco Canary Wharf — Loading Bay"
              />
            </div>
            <div>
              <label style={label}>Timezone</label>
              <select
                style={selectStyle}
                value={form.timezone}
                onChange={(e) => setField("timezone", e.target.value)}
              >
                {["Europe/London", "UTC", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Los_Angeles"].map(
                  (tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label style={label}>Address line 1</label>
              <input
                style={inputStyle}
                value={form.address_line_1}
                onChange={(e) => setField("address_line_1", e.target.value)}
              />
            </div>
            <div>
              <label style={label}>Address line 2</label>
              <input
                style={inputStyle}
                value={form.address_line_2}
                onChange={(e) => setField("address_line_2", e.target.value)}
              />
            </div>

            <div>
              <label style={label}>City</label>
              <input
                style={inputStyle}
                value={form.city}
                onChange={(e) => setField("city", e.target.value)}
              />
            </div>
            <div>
              <label style={label}>County</label>
              <input
                style={inputStyle}
                value={form.county}
                onChange={(e) => setField("county", e.target.value)}
              />
            </div>

            <div>
              <label style={label}>Country</label>
              <input
                style={inputStyle}
                value={form.country}
                onChange={(e) => setField("country", e.target.value)}
              />
            </div>
          </div>

          {/* Lat/Lon + Map */}
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label style={label}>Latitude</label>
              <input
                type="number"
                step="0.000001"
                style={inputStyle}
                value={form.lat}
                onChange={(e) => setField("lat", e.target.value === "" ? null : Number(e.target.value))}
              />
            </div>
            <div>
              <label style={label}>Longitude</label>
              <input
                type="number"
                step="0.000001"
                style={inputStyle}
                value={form.lon}
                onChange={(e) => setField("lon", e.target.value === "" ? null : Number(e.target.value))}
              />
            </div>
          </div>

          {/* Authority Panel (Auto-Populated) */}
          <div style={{ ...card, marginTop: 12 }}>
            <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 break-words" style={{ fontWeight: 800, color: accentColor }}>
                Authority (Auto-Resolved)
              </div>
              <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                {adminBusy && infoBadge("Resolving…")}
                {!adminBusy && adminArea?.gss_code && dirBusy && infoBadge("Loading details…")}
                {!adminBusy && !dirBusy && adminArea?.gss_code && authorityEntry?.is_active === false && inactiveBadge}
                {!adminBusy && !dirBusy && adminArea?.gss_code && authorityEntry?.is_active !== false && authorityEntry && activeBadge}
              </div>
            </div>

            <div style={{ marginTop: 10 }} className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div style={label}>Authority Name</div>
                <div className="text-sm font-semibold break-words">
                  {adminArea?.name || (adminBusy ? "Resolving…" : "—")}
                </div>
              </div>
              <div>
                <div style={label}>GSS Code</div>
                <div className="text-sm font-semibold break-words">
                  {adminArea?.gss_code || (adminBusy ? "Resolving…" : "—")}
                </div>
              </div>

              {adminArea?.gss_code && (
                <>
                  <div>
                    <div style={label}>Upper-Tier Authority / County</div>
                    <div className="text-sm font-semibold break-words">
                      {adminArea.has_separate_upper_tier
                        ? adminArea.ctyua_name || "—"
                        : adminArea.ctyua_gss_code
                        ? "No separate upper-tier authority"
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div style={label}>Upper-Tier GSS Code</div>
                    <div className="text-sm font-semibold break-words">
                      {adminArea.has_separate_upper_tier ? adminArea.ctyua_gss_code || "—" : "—"}
                    </div>
                  </div>
                </>
              )}

              <div>
                <div style={label}>Area Type</div>
                <div className="text-sm">{adminArea?.area_type || "—"}</div>
              </div>
              <div>
                <div style={label}>Country</div>
                <div className="text-sm">{adminArea?.country_code || "—"}</div>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              {!adminBusy && !adminArea && (
                <div className="text-sm text-gray-700">
                  {warnBadge("No authority match for this location")}
                </div>
              )}

              {!!adminError && (
                <div className="text-sm font-semibold text-red-700 break-words" style={{ marginTop: 6 }}>
                  ⚠ {adminError}
                </div>
              )}

              {adminArea?.gss_code && dirNotConfigured && (
                <div className="text-sm text-gray-700" style={{ marginTop: 6 }}>
                  {warnBadge("No authority directory entry configured")}
                </div>
              )}

              {!!dirError && (
                <div className="text-sm font-semibold text-red-700 break-words" style={{ marginTop: 6 }}>
                  ⚠ {dirError}
                </div>
              )}

              {authorityEntry && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2" style={{ marginTop: 10 }}>
                  <div>
                    <div style={label}>Usual Lead Time</div>
                    <div className="text-sm break-words">{authorityEntry?.usual_lead_time || authorityEntry?.lead_time || "—"}</div>
                  </div>
                  <div>
                    <div style={label}>Costs</div>
                    <div className="text-sm break-words">{authorityEntry?.costs || authorityEntry?.cost || "—"}</div>
                  </div>

                  <div>
                    <div style={label}>Licence Types (1–3)</div>
                    <div className="text-sm break-words">
                      {[authorityEntry?.licence_type_1, authorityEntry?.licence_type_2, authorityEntry?.licence_type_3]
                        .filter(Boolean)
                        .join(" • ") || "—"}
                    </div>
                  </div>

                  <div>
                    <div style={label}>Status</div>
                    <div className="text-sm font-semibold">
                      {authorityEntry?.is_active === false ? "Inactive" : "Active"}
                    </div>
                  </div>

                  <div>
                    <div style={label}>Licensing URL</div>
                    {safeUrl(authorityEntry?.licensing_url) ? (
                      <a
                        href={safeUrl(authorityEntry?.licensing_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm underline break-all"
                        style={{ color: accentColor }}
                      >
                        Open licensing site
                      </a>
                    ) : (
                      <div className="text-sm">—</div>
                    )}
                  </div>

                  <div>
                    <div style={label}>Permit Scheme URL</div>
                    {safeUrl(authorityEntry?.permit_scheme_url) ? (
                      <a
                        href={safeUrl(authorityEntry?.permit_scheme_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm underline break-all"
                        style={{ color: accentColor }}
                      >
                        Open permit scheme
                      </a>
                    ) : (
                      <div className="text-sm">—</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            ref={mapRef}
            style={{
              height: "clamp(220px, 36vh, 320px)",
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #e5e5e5",
              marginTop: 10,
            }}
          />
          <div className="text-xs text-gray-500 mt-1">
            Tip: Click the map to drop the pin, or drag the pin to adjust. Coordinates update automatically.
          </div>
          </>}

          {errorText && <div className="mt-2 font-semibold text-red-700 break-words">❌ {errorText}</div>}
        </div>

        {/* Footer (theme-aware) */}
        <div style={footer}>
          <button
            onClick={onClose}
            disabled={busy}
            className="w-full rounded bg-btnGeneralNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !recordReady}
            className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
            title={editing ? "Save Location" : "Create Location"}
          >
            {busy ? "Saving…" : editing ? "Save Changes" : "Create"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LocationEditModal;
